import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import Playground from "@/views/playground/Playground.vue";
import { PERMISSIONS } from "@/constants/rolesAndPermissions";
import NProgress from "nprogress";

NProgress.configure({ showSpinner: false });
const Dashboard = () => import("@/views/Dashboard/Index.vue");
const OrderReport = () =>
  import(
    "@/views/Reports/OrderProductionReport/OrderProductionReportIndex.vue"
  );
const Leads = () => import("@/views/Leads/LeadsIndex.vue");
const ProductionReviewIndex = () =>
  import("@/views/ProductionReview/ProductionReviewIndex.vue");
const About = () => import("@/views/About.vue");
const Login = () => import("@/views/Login.vue");
const WithNavigation = () => import("@/views/Layout/WithNavigation.vue");

//Settings
const Settings = () => import("@/views/Settings/index.vue");
const SettingsUserManagement = () =>
  import("@/views/Settings/UserManagement/UserManagement.vue");
const SettingsMyAccount = () =>
  import("@/views/Settings/MyAccount/Myaccount.vue");
const SettingsIntegrations = () =>
  import("@/views/Settings/Integrations/Integrations.vue");
const SettingsEmailLogs = () => import("@/views/Settings/EmailLogs/index.vue");

const FinancialControl = () =>
  import("@/views/FinancialControl/FinancialControl.vue");
const Customers = () => import("@/views/Customers/Customers.vue");
const CustomerMasterView = () =>
  import("@/views/Customers/CustomerMasterView.vue");
const CustomerDetailedView = () =>
  import("@/views/Customers/CustomerDetailedView.vue");

//Financial and Statistical Reports
const ReportsPage = () => import("@/views/Reports/ReportsPage.vue");
const SalesByDealer = () => import("@/views/Reports/SalesByDealers/index.vue");
const SalesByProducts = () =>
  import("@/views/Reports/SalesByProducts/index.vue");

//Data setup
const DataSetupIndex = () => import("@/views/DataSetup/DataSetupIndex.vue");

//Data setup - Discounts  -> main index -> sub indexes
const DataSetupDiscountIndex = () =>
  import("@/views/DataSetup/Discounts/DataSetupDiscountIndex.vue");
const DataSetupDiscountProductsIndex = () =>
  import(
    "@/views/DataSetup/Discounts/Products/DataSetupDiscountProductsIndex.vue"
  );
const DataSetupDiscountAccessoriesIndex = () =>
  import(
    "@/views/DataSetup/Discounts/Accessories/DataSetupDiscountAccessoriesIndex.vue"
  );
const DataSetupDiscountFeesIndex = () =>
  import("@/views/DataSetup/Discounts/Fees/DataSetupDiscountFeesIndex.vue");

//Data setup - Locations  -> main index -> sub indexes
const DataSetupLocationsIndex = () =>
  import("@/views/DataSetup/Locations/DataSetupLocationsIndex.vue");
const DataSetupLocationsManualFavouriteListIndex = () =>
  import(
    "@/views/DataSetup/Locations/ManualFavouriteList/DataSetupLocationsManualFavouriteListIndex.vue"
  );
const DataSetupLocationsOriginalListIndex = () =>
  import(
    "@/views/DataSetup/Locations/OriginalList/DataSetupLocationsOriginalListIndex.vue"
  );
const DataSetupLocationsSystemFavouriteListIndex = () =>
  import(
    "@/views/DataSetup/Locations/SystemFavouriteList/DataSetupLocationsSystemFavouriteListIndex.vue"
  );

const InviteUserCreatePassword = () =>
  import("@/views/Settings/UserManagement/InviteUserCreatePassword.vue");
const NotFound = () => import("@/views/NotFound.vue");

const RestPw = () => import("@/views/ResetPassword.vue");

const routes = [
  {
    path: "/login",
    name: "login",
    component: Login,
    meta: {
      guestOnly: true,
    },
  },
  {
    path: "/invitation",
    name: "createPassword",
    component: InviteUserCreatePassword,
    meta: {
      guestOnly: true,
    },
  },
  {
    path: "/reset-password/:token",
    name: "resetPassword",
    component: RestPw,
    meta: {
      guestOnly: true,
    },
  },
  {
    path: "/",
    name: "dashboard",
    component: WithNavigation,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: "/",
        name: "/",
        // component: Dashboard,
        beforeEnter: (to, from, next) => {
          if (
            store.getters["auth/authorizeUserPermission"](
              PERMISSIONS.VIEW_PRODUCTIONS
            )
          ) {
            return next({ name: "production-dashboard" });
          }

          if (
            store.getters["auth/authorizeUserPermission"](
              PERMISSIONS.FINANCIAL_CONTROLLER_DASHBOARD
            )
          ) {
            return next({ name: "financial-control" });
          }

          if (
            store.getters["auth/authorizeUserPermission"](
              PERMISSIONS.VIEW_LEADS
            )
          ) {
            return next({ name: "leads" });
          }

          if (
            store.getters["auth/authorizeUserPermission"](
              PERMISSIONS.VIEW_DEALERS
            )
          ) {
            return next({ name: "dealers" });
          }

          return next({ name: "not-found" });
        },
      },
      {
        path: "/production-dashboard",
        name: "production-dashboard",
        component: Dashboard,
        meta: {
          requiredPermission: PERMISSIONS.VIEW_PRODUCTIONS,
        },
      },
      { path: "/about", name: "about", component: About },
      { path: "/not-found", name: "not-found", component: NotFound },
      {
        path: "/settings",
        name: "settings",
        component: Settings,
        children: [
          {
            path: "user-management",
            name: "user-management",
            component: SettingsUserManagement,
            meta: {
              requiredPermission: PERMISSIONS.VIEW_USERS,
            },
          },
          {
            path: "my-account",
            name: "my-account",
            component: SettingsMyAccount,
          },
          {
            path: "integrations",
            name: "integrations",
            component: SettingsIntegrations,
            meta: {
              requiredPermission: PERMISSIONS.VIEW_INTEGRATIONS,
            },
          },
          {
            path: "email-logs",
            name: "email-logs",
            component: SettingsEmailLogs,
            meta: {
              requiredPermission: PERMISSIONS.VIEW_EMAIL_LOGS,
            },
          },
          {
            path: "discounts",
            name: "discounts",
            component: DataSetupDiscountIndex,
            meta: {
              requiredPermission: PERMISSIONS.VIEW_DISCOUNTS,
            },
            children: [
              {
                path: "products",
                name: "settings-discounts-products",
                component: DataSetupDiscountProductsIndex,
              },
              {
                path: "accessories",
                name: "settings-discounts-accessories",
                component: DataSetupDiscountAccessoriesIndex,
              },
              {
                path: "fees",
                name: "data-setup.discounts.fees.index",
                component: DataSetupDiscountFeesIndex,
              },
            ],
          },
        ],
      },
      {
        path: "/data-setup",
        name: "data-setup.index",
        component: DataSetupIndex,
        children: [
          {
            path: "discounts",
            name: "data-setup.discounts.index",
            component: DataSetupDiscountIndex,
            meta: {
              requiredPermission: PERMISSIONS.VIEW_DISCOUNTS,
            },
            children: [
              {
                path: "products",
                name: "data-setup.discounts.products.index",
                component: DataSetupDiscountProductsIndex,
              },
              {
                path: "accessories",
                name: "data-setup.discounts.accessories.index",
                component: DataSetupDiscountAccessoriesIndex,
              },
              {
                path: "fees",
                name: "data-setup.discounts.fees.index",
                component: DataSetupDiscountFeesIndex,
              },
            ],
          },
          {
            path: "locations",
            name: "locations",
            component: DataSetupLocationsIndex,
            children: [
              {
                path: "original-list",
                name: "data-setup.locations.original-list.index",
                component: DataSetupLocationsOriginalListIndex,
              },
              {
                path: "manual-favourite-list",
                name: "data-setup.locations.manual-favourite-list.index",
                component: DataSetupLocationsManualFavouriteListIndex,
              },
              {
                path: "system-favourite-list",
                name: "data-setup.locations.system-favourite-list.index",
                component: DataSetupLocationsSystemFavouriteListIndex,
              },
            ],
          },
        ],
      },
      {
        path: "/financial-control",
        name: "financial-control",
        component: FinancialControl,
        meta: {
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
        },
      },
      {
        path: "/customers",
        name: "customers",
        component: Customers,
      },
      {
        path: "/customers/:customerId",
        component: CustomerDetailedView,
        props: true,
      },
      {
        path: "/dealers",
        name: "dealers",
        component: () => import("@/views/Dealers/DealersPage.vue"),
        meta: {
          requiredPermission: PERMISSIONS.VIEW_DEALERS,
        },
      },
      {
        path: "dealers/:dealerId/approval-pending",
        component: () => import("@/views/Dealers/id/PendingDealerInfo.vue"),
        props: true,
        meta: {
          requiredPermission: PERMISSIONS.VIEW_DEALERS,
        },
        beforeEnter: (to, from, next) => {
          to.meta.backUrl =
            from.fullPath !== "/" && from.fullPath.includes("/dealers")
              ? from.fullPath
              : "/dealers";
          next();
        },
      },
      {
        path: "dealers/:dealerId/approval-rejected",
        component: () => import("@/views/Dealers/id/PendingDealerInfo.vue"),
        props: true,
        meta: {
          requiredPermission: PERMISSIONS.VIEW_DEALERS,
        },
        beforeEnter: (to, from, next) => {
          to.meta.backUrl =
            from.fullPath !== "/" && from.fullPath.includes("/dealers")
              ? from.fullPath
              : "/dealers";
          next();
        },
      },
      {
        path: "/dealers/:dealerId",
        component: () => import("@/views/Dealers/id/Dealer.vue"),
        meta: {
          requiredPermission: PERMISSIONS.VIEW_DEALERS,
        },
        beforeEnter: (to, from, next) => {
          to.meta.backUrl =
            from.fullPath !== "/" && from.fullPath.includes("/dealers")
              ? from.fullPath
              : "/dealers";
          next();
        },
        children: [
          {
            path: "information",
            component: () => import("@/views/Dealers/id/DealerInformation.vue"),
            alias: "",
            props: true,
          },
          {
            path: "statistics",
            component: () => import("@/views/Dealers/id/DealerStatistics.vue"),
            props: true,
          },
          {
            path: "users",
            component: () => import("@/views/Dealers/id/DealerUsers.vue"),
            props: true,
          },
          {
            path: "orders",
            component: () => import("@/views/Dealers/id/DealerOrders.vue"),
            props: true,
          },
          {
            path: "customers",
            component: () => import("@/views/Dealers/id/DealerCustomers.vue"),
            props: true,
            children: [
              {
                path: ":customerId",
                component: () =>
                  import("@/views/Customers/CustomerDetailedView.vue"),
                props: (route) => ({
                  customerId: route.params.customerId,
                }),
              },
            ],
          },
          {
            path: "discounts",
            component: () =>
              import("@/views/Dealers/id/discounts/DealerDiscounts.vue"),
            props: true,
          },
          {
            path: "qp-branding",
            component: () => import("@/views/Dealers/id/QPBranding.vue"),
            props: true,
          },
          {
            path: "quoting_portal",
            component: () => import("@/views/Dealers/id/QuotingPortal.vue"),
            props: true,
          },
        ],
      },
      {
        path: "/reports",
        name: "reports",
        component: ReportsPage,
        children: [
          {
            path: "/sales-by-dealers",
            name: "sales-by-dealers",
            component: SalesByDealer,
          },
          {
            path: "/sales-by-products",
            name: "sales-by-products",
            component: SalesByProducts,
          },
        ],
      },
      {
        path: "/reports/sales-by-dealers",
        name: "sales-by-dealers",
        component: SalesByDealer,
      },
      {
        path: "/reports/sales-by-products",
        name: "sales-by-products",
        component: SalesByProducts,
      },
      {
        path: "/order-report/:id",
        name: "order-report",
        component: OrderReport,
        meta: {
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
        },
      },
      {
        path: "/production-review/:id",
        name: "production-review",
        component: ProductionReviewIndex,
      },
      {
        path: "leads",
        name: "leads",
        component: Leads,
        meta: {
          requiredPermission: PERMISSIONS.VIEW_LEADS,
        },
      },
      {
        path: "/leads/:id",
        name: "LeadDetails",
        component: () => import("@/views/Leads/LeadDetails.vue"),
        props: (route) => ({
          id: route.params.id,
        }),
      },
    ],
  },
  {
    path: "/playground",
    name: "playground",
    component: Playground,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
});

router.beforeEach(async (to, from) => {
  NProgress.start();
  store.dispatch("preloader/setRouteLoading", true);

  const isAuthenticated = store.getters["auth/isAuthenticated"];
  const currentUser = store.getters["auth/user"];

  if (to.meta.requiresAuth && !to.meta.guestOnly && isAuthenticated) {
    if (currentUser.email) {
      store.dispatch("auth/refreshCurrentUser");
    } else {
      console.log("refreshing current user");
      await store.dispatch("auth/refreshCurrentUser");
    }
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    return { name: "login" };
  } else if (to.meta.guestOnly && isAuthenticated) {
    return from;
  }

  const requiredPermission = to.meta.requiredPermission;

  if (to.meta.requiresAuth && isAuthenticated && requiredPermission) {
    const isAuthorized =
      store.getters["auth/authorizeUserPermission"](requiredPermission);
    if (!isAuthorized) {
      return { name: "not-found" };
    }
  }
});

router.afterEach(() => {
  const currentUser = store.getters["auth/user"];
  const timeout = currentUser?.email ? 1000 : 0;
  setTimeout(() => {
    NProgress.done();
    store.dispatch("preloader/setRouteLoading", false);
  }, timeout);
});

export default router;
